import { useEffect, useRef, useState, type KeyboardEvent, type TouchEvent } from "react"
import { readableQuestion, type QuestionOption } from "./questionOptions"
import "../../nira2/case/case.css"
import "../decisions.css"
import "./checkpointCard.css"
import "./documentCard.css"
import "./cards.css"

export type BatchQuestion = { id: string; question: string; why?: string; options?: QuestionOption[]; allegation_id?: string; quotes?: string[] }

/** Quotes that read as a short passage stay open; a long set folds behind a summary. */
const QUOTES_OPEN_MAX = 280
const SWIPE_PX = 48

type Draft = { picked: number | null; text: string }
const EMPTY: Draft = { picked: null, text: "" }

/** The answer a draft stands for, or "" when the question is still open. */
function answerOf(draft: Draft, options: QuestionOption[]): string {
  if (options.length > 0 && draft.picked !== null && draft.picked >= 0) return options[draft.picked]?.label ?? ""
  return draft.text.trim()
}

/** The one message that carries every answer back to the agent: a line per question, in order. */
export function batchAnswerMessage(rows: { question: string; answer: string }[]): string {
  const lines = rows.map((r, i) => `${i + 1}. ${r.question}\nAnswer: ${r.answer}`)
  return `My answers to your ${rows.length} questions:\n\n${lines.join("\n\n")}`
}

/**
 * Several officer questions at once (one batch from the agent): stacked side by side, one in view,
 * with previous/next, a position, step dots, arrow keys and swipe. Each card is the single question
 * card's form. Nothing is sent until every question has an answer; then one message carries them all.
 */
export function BatchQuestionCard({ heading, questions, send, disabled, onAnswered }: {
  heading: string
  questions: BatchQuestion[]
  send: (message: string) => Promise<boolean>
  disabled?: boolean
  onAnswered?: () => void
}) {
  const views = questions.map((q) => ({ q, v: readableQuestion({ question: q.question, why: q.why, options: q.options }) }))
  const [drafts, setDrafts] = useState<Draft[]>(() => views.map(({ v }) => {
    const d = v.options.findIndex((o) => o.default)
    return { picked: d >= 0 ? d : null, text: "" }
  }))
  const [at, setAt] = useState(0)
  const [pending, setPending] = useState(false)
  const [failed, setFailed] = useState(false)
  const blocked = disabled || pending
  const heads = useRef<(HTMLHeadingElement | null)[]>([])
  const boxes = useRef<(HTMLTextAreaElement | null)[]>([])
  const moved = useRef(false)
  const touch = useRef<{ x: number; y: number } | null>(null)
  const total = views.length
  const answers = views.map(({ v }, i) => answerOf(drafts[i] ?? EMPTY, v.options))
  const done = answers.filter(Boolean).length
  const ready = done === total

  // Move focus to the new card after the reader navigates; not on first render.
  useEffect(() => {
    if (!moved.current) return
    heads.current[at]?.focus({ preventScroll: true })
  }, [at])
  useEffect(() => {
    const d = drafts[at]
    if (moved.current && d?.picked === -1) boxes.current[at]?.focus()
  }, [drafts, at])

  const go = (to: number) => {
    const next = Math.max(0, Math.min(total - 1, to))
    if (next === at) return
    moved.current = true
    setAt(next)
  }
  const edit = (i: number, patch: Partial<Draft>) => {
    moved.current = true
    setDrafts((all) => all.map((d, j) => (j === i ? { ...d, ...patch } : d)))
    if (failed) setFailed(false)
  }
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    const t = e.target as HTMLElement
    // Inside a field the arrows belong to the field (caret, radio choice).
    if (t.tagName === "TEXTAREA" || t.tagName === "INPUT") return
    if (e.key === "ArrowRight") { e.preventDefault(); go(at + 1) }
    else if (e.key === "ArrowLeft") { e.preventDefault(); go(at - 1) }
  }
  const onTouchStart = (e: TouchEvent) => { const t = e.touches[0]; touch.current = t ? { x: t.clientX, y: t.clientY } : null }
  const onTouchEnd = (e: TouchEvent) => {
    const from = touch.current
    const t = e.changedTouches[0]
    touch.current = null
    if (!from || !t) return
    const dx = t.clientX - from.x
    if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > Math.abs(t.clientY - from.y) * 1.5) go(at + (dx < 0 ? 1 : -1))
  }
  const submit = async () => {
    if (!ready || blocked) return
    setPending(true)
    setFailed(false)
    let sent = false
    try {
      sent = await send(batchAnswerMessage(views.map(({ v }, i) => ({ question: v.question, answer: answers[i] }))))
    } catch {
      sent = false
    } finally {
      setPending(false)
    }
    if (sent) onAnswered?.()
    else setFailed(true)
  }

  return (
    <section className="nsd-cp osq osq-batch" aria-label="Questions" onKeyDown={onKey}>
      <div className="osq-bhead">
        <p className="ndc-ov">Questions</p>
        <p className="osq-pos" aria-live="polite">{at + 1} of {total}</p>
      </div>
      <p className="ndc-ask">{heading}</p>
      <div className="osq-view" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="osq-track" style={{ transform: `translateX(-${at * 100}%)` }}>
          {views.map(({ q, v }, i) => {
            const draft = drafts[i] ?? EMPTY
            const choices = v.options
            const active = i === at
            const quotes = q.quotes ?? []
            const long = quotes.join(" ").length > QUOTES_OPEN_MAX
            const group = `osq-${q.id}`
            return (
              <div
                key={q.id}
                className="osq-slide"
                role="group"
                aria-roledescription="question"
                aria-label={`Question ${i + 1} of ${total}`}
                aria-hidden={active ? undefined : true}
                style={active ? undefined : { visibility: "hidden" }}
              >
                <h3 className="ndc-ask osq-q" tabIndex={-1} ref={(el) => { heads.current[i] = el }}>
                  {q.allegation_id ? <span className="osq-aid">{q.allegation_id}</span> : null}{v.question}
                </h3>
                {v.why ? <p className="ndc-why">{v.why}</p> : null}
                {quotes.length > 0 ? (
                  <details className="osq-quotes" open={!long}>
                    <summary>From the record ({quotes.length})</summary>
                    {quotes.map((t, k) => <blockquote key={k}>{t}</blockquote>)}
                  </details>
                ) : null}
                <div className="osq-reply">
                  {choices.length > 0 ? (
                    <div className="osq-opts" role="radiogroup" aria-label={`Your answer options for question ${i + 1}`}>
                      {choices.map((o, k) => (
                        <label key={o.label} className={`osq-opt${draft.picked === k ? " on" : ""}`}>
                          <input type="radio" name={group} checked={draft.picked === k} disabled={blocked} onChange={() => edit(i, { picked: k })} />
                          <span className="osq-opt-t">
                            <span className="osq-opt-l">{o.label}{o.default ? <span className="osq-rec">Recommended</span> : null}</span>
                            {o.hint ? <span className="osq-opt-h">{o.hint}</span> : null}
                          </span>
                        </label>
                      ))}
                      <label className={`osq-opt${draft.picked === -1 ? " on" : ""}`}>
                        <input type="radio" name={group} checked={draft.picked === -1} disabled={blocked} onChange={() => edit(i, { picked: -1 })} />
                        <span className="osq-opt-t"><span className="osq-opt-l">Something else</span></span>
                      </label>
                    </div>
                  ) : null}
                  <textarea
                    ref={(el) => { boxes.current[i] = el }}
                    className="nsd-in osq-in"
                    rows={2}
                    aria-label={`Your answer to question ${i + 1}`}
                    placeholder={choices.length > 0 ? "Or type your answer" : "Type your answer"}
                    value={draft.text}
                    disabled={blocked}
                    tabIndex={active ? undefined : -1}
                    onChange={(e) => edit(i, { text: e.target.value, ...(choices.length > 0 && e.target.value.trim() ? { picked: -1 } : {}) })}
                  />
                  {answers[i] && !active ? <p className="osq-got">Your answer: <strong>{answers[i]}</strong></p> : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div className="osq-nav">
        <button type="button" className="ncf-btn" aria-label="Previous question" disabled={at === 0} onClick={() => go(at - 1)}>Previous</button>
        <div className="osq-dots" role="group" aria-label="Questions">
          {views.map(({ q }, i) => (
            <button
              key={q.id}
              type="button"
              className={`osq-dot${i === at ? " on" : ""}${answers[i] ? " done" : ""}`}
              aria-label={`Question ${i + 1}${answers[i] ? ", answered" : ", not answered"}`}
              aria-current={i === at ? "step" : undefined}
              onClick={() => go(i)}
            />
          ))}
        </div>
        <button type="button" className="ncf-btn" aria-label="Next question" disabled={at === total - 1} onClick={() => go(at + 1)}>Next</button>
      </div>
      <div className="nsd-stop-acts">
        <button type="button" className="ncf-btn primary" disabled={blocked || !ready} onClick={() => void submit()}>Answer all</button>
        <span className="osq-prog">{done} of {total} answered</span>
        {failed ? <span className="osq-err" role="alert">Couldn't reach the simulator. Try again.</span> : null}
      </div>
    </section>
  )
}
